import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Instagram, Facebook, Youtube, ShoppingBag, Music2 } from "lucide-react";

export function usePublicSettings() {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  return settings;
}

function TrackingScripts({ settings }) {
  useEffect(() => {
    if (!settings) return;
    if (settings.gtm_id && /^GTM-[A-Z0-9]+$/i.test(settings.gtm_id)) {
      if (!document.getElementById("gtm-script")) {
        const s = document.createElement("script");
        s.id = "gtm-script";
        s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${settings.gtm_id}');`;
        document.head.appendChild(s);
      }
    }
    if (settings.meta_pixel_id && /^\d{5,}$/.test(settings.meta_pixel_id)) {
      if (!document.getElementById("fb-pixel")) {
        const s = document.createElement("script");
        s.id = "fb-pixel";
        s.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${settings.meta_pixel_id}');fbq('track','PageView');`;
        document.head.appendChild(s);
      }
    }
  }, [settings]);
  return null;
}

export default function PublicLayout({ children }) {
  const settings = usePublicSettings();
  const [nav, setNav] = useState([]);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    api.get("/public/nav").then((r) => setNav(r.data)).catch(() => {});
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const title = settings?.site_title || "EmotionalSignal";
  const socials = [
    { key: "social_instagram", icon: Instagram, label: "Instagram" },
    { key: "social_facebook", icon: Facebook, label: "Facebook" },
    { key: "social_youtube", icon: Youtube, label: "YouTube" },
    { key: "social_tiktok", icon: Music2, label: "TikTok" },
    { key: "social_etsy", icon: ShoppingBag, label: "Etsy" },
  ].filter((s) => settings?.[s.key]);

  return (
    <div className="grain min-h-screen bg-[#FCFCFA] text-[#0A0A0A]" dir="ltr">
      <TrackingScripts settings={settings} />
      <header
        data-testid="public-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-300 ${
          scrolled ? "bg-white/80 backdrop-blur-xl border-b border-black/5 py-4" : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between">
          <Link to="/" data-testid="public-logo" className="font-display text-2xl font-bold tracking-tight">
            {title}
          </Link>
          <nav className="hidden md:flex items-center gap-8 font-body text-sm">
            <Link to="/" className="hover:opacity-60 transition-opacity">Home</Link>
            {nav.map((p) => (
              <Link key={p.slug} to={`/p/${p.slug}`} className="hover:opacity-60 transition-opacity" data-testid={`nav-link-${p.slug}`}>
                {p.title}
              </Link>
            ))}
            {settings?.social_etsy && (
              <a href={settings.social_etsy} target="_blank" rel="noreferrer"
                 className="bg-[#0A0A0A] text-white px-5 py-2 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-transform">
                Shop on Etsy
              </a>
            )}
          </nav>
        </div>
      </header>

      <main className="relative z-10">{children}</main>

      <footer className="relative z-10 border-t border-black/10 mt-24 py-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-10">
          <div>
            <p className="font-display text-3xl font-bold">{title}</p>
            {settings?.site_description && (
              <p className="font-body text-[#555] mt-3 max-w-sm font-light">{settings.site_description}</p>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex gap-3">
              {socials.map((s) => {
                const Icon = s.icon;
                return (
                  <a key={s.key} href={settings[s.key]} target="_blank" rel="noreferrer"
                     data-testid={`footer-social-${s.label.toLowerCase()}`}
                     className="w-10 h-10 rounded-full border border-black/15 flex items-center justify-center hover:bg-black hover:text-white transition-colors">
                    <Icon className="w-4 h-4" strokeWidth={1.6} />
                  </a>
                );
              })}
            </div>
            {settings?.contact_email && (
              <a href={`mailto:${settings.contact_email}`} className="font-body text-sm text-[#555] hover:text-black transition-colors">
                {settings.contact_email}
              </a>
            )}
            <p className="font-body text-xs text-[#999]">© {new Date().getFullYear()} {title}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
